import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import NodeWrapper from "./NodeWrapper";
import useHandleConnected from "./useHandleConnected";
import { AccountTree } from "@mui/icons-material";

/**
 * IF Avancado — Multiplas condicoes com AND/OR e N saidas.
 *
 * data.conditions: [
 *   { leftValue, operator, rightValue }
 * ]
 * data.logicOperator: "AND" | "OR"
 *
 * Saidas: handle "true" (condicao atendida), handle "false" (nao atendida)
 */
const OPERATORS_LABEL = {
  equals: "==",
  notEquals: "!=",
  contains: "contem",
  notContains: "nao contem",
  startsWith: "inicia com",
  endsWith: "termina com",
  greaterThan: ">",
  lessThan: "<",
  greaterOrEqual: ">=",
  lessOrEqual: "<=",
  isEmpty: "vazio",
  isNotEmpty: "nao vazio",
  regex: "regex",
};

const IfAdvancedNode = memo(({ id, data, isConnectable, selected }) => {
  const getHandleClass = useHandleConnected(id);
  const conditions = data?.conditions || [];
  const logic = data?.logicOperator || "AND";

  return (
    <NodeWrapper
      id={id}
      data={data}
      icon={<AccountTree />}
      title="IF Avancado"
      color="#E65100"
      bgColor="#FFF3E0"
      noSource
      isConnectable={isConnectable}
      selected={selected}
      minWidth={240}
    >
      {/* Logic operator badge */}
      {conditions.length > 1 && (
        <div style={{
          display: "inline-block",
          background: logic === "AND" ? "#1565C0" : "#7B1FA2",
          color: "#fff",
          borderRadius: 4,
          padding: "1px 8px",
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 6,
          letterSpacing: "0.05em",
        }}>
          {logic}
        </div>
      )}

      {/* Conditions list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {conditions.length === 0 ? (
          <div style={{ fontSize: 11, color: "#999", fontStyle: "italic" }}>
            Nenhuma condicao configurada
          </div>
        ) : (
          conditions.slice(0, 3).map((c, i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                padding: "3px 8px",
                fontSize: 11,
                fontFamily: "'Fira Code', monospace",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span style={{ color: "#1565C0", fontWeight: 600 }}>
                {c.leftValue || "?"}
              </span>
              <span style={{
                color: "#fff",
                background: "#555",
                borderRadius: 3,
                padding: "0 4px",
                fontSize: 9,
                fontWeight: 700,
              }}>
                {OPERATORS_LABEL[c.operator] || c.operator || "?"}
              </span>
              {c.operator !== "isEmpty" && c.operator !== "isNotEmpty" && (
                <span style={{ color: "#C62828", fontWeight: 600 }}>
                  {c.rightValue || "?"}
                </span>
              )}
            </div>
          ))
        )}
        {conditions.length > 3 && (
          <div style={{ fontSize: 10, color: "#999" }}>
            +{conditions.length - 3} condicao(oes)
          </div>
        )}
      </div>

      {/* TRUE / FALSE handles */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 6, position: "relative", paddingRight: 4,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff",
            background: "#4caf50", borderRadius: 4,
            padding: "2px 10px", letterSpacing: "0.04em",
          }}>
            VERDADEIRO
          </span>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            className={getHandleClass("source", "true")}
            style={{
              position: "absolute", top: "50%", right: -13,
              transform: "translateY(-50%)",
              cursor: "crosshair",
            }}
            isConnectable={isConnectable}
          />
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 6, position: "relative", paddingRight: 4,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff",
            background: "#ef5350", borderRadius: 4,
            padding: "2px 10px", letterSpacing: "0.04em",
          }}>
            FALSO
          </span>
          <Handle
            type="source"
            position={Position.Right}
            id="false"
            className={getHandleClass("source", "false")}
            style={{
              position: "absolute", top: "50%", right: -13,
              transform: "translateY(-50%)",
              cursor: "crosshair",
            }}
            isConnectable={isConnectable}
          />
        </div>
      </div>
    </NodeWrapper>
  );
});

IfAdvancedNode.displayName = "IfAdvancedNode";
export default IfAdvancedNode;
